import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, FileText, Loader2, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, formatErr } from "@/lib/api";
import { Panel } from "@/components/admin/Panel";

const LABELS = { id_front: "ID Front", id_back: "ID Back", selfie: "Selfie with ID" };

const Doc = ({ file, onDeleted }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    if (file.is_deleted) return undefined;
    api.get(`/admin/files/${file.id}`, { responseType: "blob" })
      .then((r) => {
        u = URL.createObjectURL(r.data);
        setUrl(u);
      })
      .catch(() => setUrl("error"));
    return () => u && URL.revokeObjectURL(u);
  }, [file.id, file.is_deleted]);

  const del = async () => {
    try {
      await api.delete(`/admin/files/${file.id}`);
      toast.success("ID file permanently deleted");
      onDeleted();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const isPdf = file.content_type === "application/pdf";
  return (
    <div className="space-y-2" data-testid={`id-doc-${file.kind}`}>
      <p className="text-sm font-bold">{LABELS[file.kind]}</p>
      <div className="aspect-[4/3] rounded-xl border border-border bg-secondary overflow-hidden grid place-items-center">
        {file.is_deleted ? <span className="text-sm text-muted-foreground" data-testid={`id-doc-deleted-${file.kind}`}>Deleted after verification</span>
          : !url ? <Loader2 className="h-6 w-6 animate-spin text-primary" />
          : url === "error" ? <span className="text-sm text-destructive">Unable to load</span>
          : isPdf ? <FileText className="h-10 w-10 text-muted-foreground" />
          : <img src={url} alt={LABELS[file.kind]} className="h-full w-full object-cover" />}
      </div>
      {!file.is_deleted && url && url !== "error" && (
        <div className="flex gap-2">
          <a href={url} target="_blank" rel="noreferrer" className="flex-1 h-9 rounded-full border border-border text-xs font-semibold inline-flex items-center justify-center gap-1 hover:bg-secondary" data-testid={`id-doc-open-${file.kind}`}><ExternalLink className="h-3 w-3" />Open</a>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="h-9 px-3 rounded-full border border-destructive/50 text-destructive text-xs font-semibold inline-flex items-center gap-1 hover:bg-destructive/10" data-testid={`id-doc-delete-${file.kind}`}><Trash2 className="h-3 w-3" />Delete</button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this ID file?</AlertDialogTitle>
                <AlertDialogDescription>The encrypted file will be wiped from storage. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={del} data-testid={`id-doc-delete-confirm-${file.kind}`}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
};

export const IdDocs = ({ files, onChange }) => (
  <Panel title="ID verification" testId="panel-id-docs">
    <div className="grid gap-4 sm:grid-cols-3">
      {["id_front", "id_back", "selfie"].map((k) => {
        const f = files.find((x) => x.kind === k);
        return f ? <Doc key={k} file={f} onDeleted={onChange} /> : null;
      })}
    </div>
  </Panel>
);
